import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Descriptions,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Popconfirm,
  Radio,
  Result,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ApiOutlined,
  BookOutlined,
  CheckCircleOutlined,
  KeyOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { usePermissions } from '../../assets/utils/usePermissions';
import { useAuth } from '../../assets/utils/useAuth';
import { isPlatformPermission, isPlatformRole } from '../../assets/utils/permissions';
import {
  ROLE_USER_TYPES,
  careAdminHomeSelectOptions,
  getAccessModule,
  modulesForPermission,
} from '../../assets/utils/accessCatalog';
import AffectsCell from './AffectsCell';
import RolePermissionAssigner from './RolePermissionAssigner';
import RbacGlossaryTab from './RbacGlossaryTab';
import AccessModulesTab from './AccessModulesTab';
import CatalogTab from './CatalogTab';
import {
  draftActionSlug,
  slugify,
  suggestResourceForModule,
  validateActionDraft,
  validateModuleDraft,
} from './catalogFormHelpers';
import {
  assignPermissionsToRole,
  cleanupRbacSystem,
  createModuleAction,
  createPermissionModule,
  createPermissionRecord,
  createRole,
  listPermissionModules,
  listModuleActions,
  deletePermissionRecord,
  deleteRole,
  fetchPermissions,
  getRoleDetails,
  getPermissionDetails,
  listRoles,
  removePermissionFromRole,
  runRbacSeeder,
  togglePermissionStatus,
  toggleRoleStatus,
  toggleRoleSystemFlag,
  updatePermissionRecord,
  updateRole,
  verifyRbacSystem,
} from '../dash/rbacApi';
import DashboardHero from '../dashboard/components/DashboardHero';
import DashboardSection from '../dashboard/components/DashboardSection';
import { ADMIN_REVIEW_MODAL_CLASS, hasMetricValue } from '../dashboard/utils';
import './rbac-dashboard.css';

const { Text } = Typography;

const userTypeOptions = ROLE_USER_TYPES.map((t) => ({ value: t.key, label: t.label }));

const roleTypeSegments = ROLE_USER_TYPES.map((t) => ({
  value: t.key,
  label: ({
    staff: 'Staff',
    admin: 'Admin',
    facility: 'Facility',
    client: 'Enrollee',
    corporation: 'Employer',
  })[t.key] || t.label,
}));

const defaultPagination = {
  current: 1,
  pageSize: 10,
  total: 0,
};

const statusFilterOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const PERMISSION_PAGE_SIZE = 1000;

const normalizeListPayload = (payload) => {
  if (!payload) return { records: [], meta: {} };
  const container = payload?.data ?? payload;
  const records =
    container?.data ??
    container?.items ??
    container?.results ??
    container?.list ??
    container ??
    [];
  const meta = payload?.pagination || container?.meta || container?.pagination || {};
  return {
    records: Array.isArray(records) ? records : [],
    meta,
  };
};

const fetchAllPermissionRecords = async (query = {}) => {
  const limit = PERMISSION_PAGE_SIZE;
  const records = [];
  let page = 1;
  let total = Infinity;

  while (page <= 50 && records.length < total) {
    const response = await fetchPermissions({ ...query, limit, page });
    const { records: batch, meta } = normalizeListPayload(response);
    const pagination = response?.pagination || meta || {};
    total = Number(pagination.total);
    if (!Number.isFinite(total) || total < 0) {
      total = records.length + batch.length;
    }
    records.push(...batch);
    if (!batch.length || batch.length < limit || records.length >= total) {
      break;
    }
    page += 1;
  }

  return records;
};

const RBACAdmin = () => {
  const { canStayOnModule } = usePermissions();
  const { refreshProfile, refreshAccessCatalog } = useAuth();

  // Write surface of the RBAC admin page. canAccess('rbacAdmin') resolves
  // admin.role_management and hasPermission already honours admin.* / platform.* /
  // platform.operator / '*', so the hand-rolled wildcard chain is gone. The legacy
  // super user (1) still gets in while their RBAC map is empty.
  const canManageRBAC = canStayOnModule('rbacAdmin', [1]);

  const [activeTab, setActiveTab] = useState('roles');

  // Roles state
  const [roles, setRoles] = useState([]);
  const [roleLoading, setRoleLoading] = useState(false);
  const [roleSearch, setRoleSearch] = useState('');
  const [roleUserType, setRoleUserType] = useState('staff');
  const [roleStatus, setRoleStatus] = useState();
  const [rolePagination, setRolePagination] = useState(defaultPagination);
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm] = Form.useForm();
  const [roleStatusUpdating, setRoleStatusUpdating] = useState(false);
  const [roleSystemUpdating, setRoleSystemUpdating] = useState(false);

  // Permissions state
  const [permissions, setPermissions] = useState([]);
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [permissionSearch, setPermissionSearch] = useState('');
  const [permissionResource, setPermissionResource] = useState();
  const [permissionStatus, setPermissionStatus] = useState();
  const [permissionModalVisible, setPermissionModalVisible] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [permissionForm] = Form.useForm();
  const [catalogEnabled, setCatalogEnabled] = useState(true);
  const [catalogModules, setCatalogModules] = useState([]);
  const [catalogActions, setCatalogActions] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogActionsLoading, setCatalogActionsLoading] = useState(false);
  const [selectedCatalogModule, setSelectedCatalogModule] = useState(undefined);
  const [permissionStatusUpdating, setPermissionStatusUpdating] = useState(false);
  // Extending the catalog itself, from inside the New-permission form.
  const [moduleDraftOpen, setModuleDraftOpen] = useState(false);
  const [actionDraftOpen, setActionDraftOpen] = useState(false);
  const [catalogSaving, setCatalogSaving] = useState(false);
  const [allCatalogActions, setAllCatalogActions] = useState([]);
  const [moduleDraftForm] = Form.useForm();
  const [actionDraftForm] = Form.useForm();

  // Role permission assignment
  const [permissionDrawerVisible, setPermissionDrawerVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [assignedPermissionKeys, setAssignedPermissionKeys] = useState([]);
  const [initialPermissionKeys, setInitialPermissionKeys] = useState([]);
  const [permissionSyncLoading, setPermissionSyncLoading] = useState(false);
  const [catalogPermissions, setCatalogPermissions] = useState([]);
  const [glossaryRoles, setGlossaryRoles] = useState([]);
  const [glossaryLoading, setGlossaryLoading] = useState(false);

  // Seeder
  const [seederLoading, setSeederLoading] = useState(false);
  const [seederResult, setSeederResult] = useState(null);

  // Cleanup
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [cleanupModalVisible, setCleanupModalVisible] = useState(false);
  const [cleanupScope, setCleanupScope] = useState('assignments');
  const [cleanupConfirmText, setCleanupConfirmText] = useState('');

  // Verification
  const [verificationData, setVerificationData] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationHealth, setVerificationHealth] = useState('loading'); // 'healthy' | 'warning' | 'error' | 'loading'

  const debounceTimeouts = React.useRef({});
  const rolePaginationRef = React.useRef(rolePagination);

  // Keep ref in sync with state
  useEffect(() => {
    rolePaginationRef.current = rolePagination;
  }, [rolePagination]);

  const debounce = (key, fn, delay = 500) => {
    if (debounceTimeouts.current[key]) {
      clearTimeout(debounceTimeouts.current[key]);
    }
    debounceTimeouts.current[key] = setTimeout(fn, delay);
  };

  const loadRoles = useCallback(
    async (params = {}) => {
      setRoleLoading(true);
      try {
        // Use ref to get current pagination values to avoid stale closures
        const currentPagination = rolePaginationRef.current;
        const page = params.page !== undefined ? params.page : currentPagination.current;
        const limit = params.limit !== undefined ? params.limit : currentPagination.pageSize;
        
        const response = await listRoles({
          search: roleSearch || undefined,
          user_type: roleUserType || undefined,
          status: roleStatus || undefined,
          page,
          limit,
        });
        const { records, meta } = normalizeListPayload(response);
        
        // Handle pagination from response - API returns pagination at root level
        const paginationData = response?.pagination || meta;
        // Hide CLI-only platform_operator from role management UI
        const visibleRoles = (Array.isArray(records) ? records : []).filter(
          (role) => !isPlatformRole(role?.name)
        );
        setRoles(visibleRoles);
        setRolePagination({
          current: paginationData?.current_page ?? paginationData?.page ?? page,
          pageSize: paginationData?.per_page ?? paginationData?.limit ?? limit,
          total: paginationData?.total ?? visibleRoles.length ?? 0,
        });
      } catch (error) {
        console.error('Failed to load roles:', error);
        notification.error({
          message: 'Unable to load roles',
          description: error?.response?.data?.message || error?.message,
          placement: 'topLeft',
        });
      } finally {
        setRoleLoading(false);
      }
    },
    [roleSearch, roleUserType, roleStatus]
  );

  const loadPermissions = useCallback(
    async () => {
      setPermissionLoading(true);
      try {
        const records = await fetchAllPermissionRecords({
          resource: permissionResource || undefined,
          search: permissionSearch || undefined,
          status: permissionStatus || undefined,
        });
        // Hide CLI-only platform.* from assignable permission lists
        setPermissions(
          records.filter((permission) => !isPlatformPermission(permission?.name))
        );
      } catch (error) {
        console.error('Failed to load permissions:', error);
        notification.error({
          message: 'Unable to load permissions',
          description: error?.response?.data?.message || error?.message,
          placement: 'topLeft',
        });
      } finally {
        setPermissionLoading(false);
      }
    },
    [permissionResource, permissionSearch, permissionStatus]
  );

  const loadCatalogPermissions = useCallback(async () => {
    try {
      const records = await fetchAllPermissionRecords({ status: 'all' });
      const visible = records.filter(
        (permission) => !isPlatformPermission(permission?.name)
      );
      setCatalogPermissions(visible);
      return visible;
    } catch (error) {
      console.error('Failed to load permission catalog:', error);
      return [];
    }
  }, []);

  const loadGlossary = useCallback(async () => {
    setGlossaryLoading(true);
    try {
      await loadCatalogPermissions();
      const response = await listRoles({ limit: 200, status: 'all' });
      const { records } = normalizeListPayload(response);
      const visible = (Array.isArray(records) ? records : []).filter(
        (role) => !isPlatformRole(role?.name)
      );
      const detailed = await Promise.all(
        visible.map(async (role) => {
          const roleId = role?.id || role?.role_id;
          if (!roleId) return role;
          try {
            const res = await getRoleDetails(roleId);
            return res?.data || res || role;
          } catch (error) {
            return role;
          }
        })
      );
      setGlossaryRoles(detailed);
    } catch (error) {
      console.error('Failed to load RBAC glossary:', error);
      notification.error({
        message: 'Unable to load glossary',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    } finally {
      setGlossaryLoading(false);
    }
  }, [loadCatalogPermissions]);

  useEffect(() => {
    if (!canManageRBAC) return;
    // Reset pagination to page 1 when filters change
    setRolePagination((prev) => {
      const updated = { ...prev, current: 1 };
      rolePaginationRef.current = updated; // Update ref immediately
      return updated;
    });
    debounce('roles', () => loadRoles({ page: 1 }), 400);
  }, [canManageRBAC, roleSearch, roleUserType, roleStatus, loadRoles]);

  useEffect(() => {
    if (!canManageRBAC) return;
    debounce('permissions', () => loadPermissions(), 400);
  }, [canManageRBAC, permissionResource, permissionSearch, permissionStatus, loadPermissions]);

  useEffect(() => {
    if (!canManageRBAC || activeTab !== 'glossary') return;
    loadGlossary();
  }, [canManageRBAC, activeTab, loadGlossary]);

  useEffect(() => () => {
    Object.values(debounceTimeouts.current).forEach(clearTimeout);
  }, []);

  const hydrateRoleForm = async (role) => {
    const roleId = role?.id || role?.role_id;
    if (!roleId) return role;
    try {
      const response = await getRoleDetails(roleId);
      return response?.data || response || role;
    } catch (error) {
      return role;
    }
  };

  const handleOpenRoleModal = async (role = null) => {
    let targetRole = role;
    if (role) {
      targetRole = await hydrateRoleForm(role);
    }
    setEditingRole(targetRole);
    if (targetRole) {
      roleForm.setFieldsValue({
        name: targetRole.name,
        display_name: targetRole.display_name,
        description: targetRole.description,
        user_type: targetRole.user_type,
        home_module: targetRole.home_module || undefined,
        home_priority: Number(targetRole.home_priority || 0),
        is_active: Number(targetRole.is_active) === 1,
        is_system_role: Boolean(targetRole.is_system_role),
      });
    } else {
      roleForm.resetFields();
      roleForm.setFieldsValue({
        is_active: true,
        is_system_role: false,
        home_priority: 0,
      });
    }
    setRoleModalVisible(true);
  };

  const handleSubmitRole = async () => {
    try {
      const values = await roleForm.validateFields();
      const payload = {
        name: values.name,
        display_name: values.display_name,
        description: values.description,
        user_type: values.user_type,
        home_module: values.home_module || null,
        home_priority: Number(values.home_priority || 0),
      };
      if (editingRole) {
        const roleId = editingRole.id || editingRole.role_id;
        await updateRole(roleId, payload);
        const desiredStatus = values.is_active ? 'active' : 'inactive';
        const currentStatus = Number(editingRole.is_active) === 1 ? 'active' : 'inactive';
        if (desiredStatus !== currentStatus) {
          await toggleRoleStatus(roleId, desiredStatus);
        }
        const desiredSystemFlag = Boolean(values.is_system_role);
        const currentSystemFlag = Boolean(editingRole.is_system_role);
        if (desiredSystemFlag !== currentSystemFlag) {
          await toggleRoleSystemFlag(roleId, desiredSystemFlag);
        }
        notification.success({ message: 'Role updated successfully', placement: 'topLeft' });
      } else {
        await createRole(payload);
        notification.success({ message: 'Role created successfully', placement: 'topLeft' });
      }
      setRoleModalVisible(false);
      setEditingRole(null);
      roleForm.resetFields();
      loadRoles();
    } catch (error) {
      if (error?.errorFields) return; // validation errors
      notification.error({
        message: editingRole ? 'Failed to update role' : 'Failed to create role',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    }
  };

  const handleDeleteRole = async (role) => {
    try {
      await deleteRole(role.id || role.role_id);
      notification.success({ message: 'Role deleted', placement: 'topLeft' });
      loadRoles();
    } catch (error) {
      notification.error({
        message: 'Failed to delete role',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    }
  };

  const hydratePermissionForm = async (permission) => {
    const permissionId = permission?.id || permission?.permission_id;
    if (!permissionId) return permission;
    try {
      const response = await getPermissionDetails(permissionId);
      return response?.data || response || permission;
    } catch (error) {
      return permission;
    }
  };

  const handleOpenPermissionModal = async (record = null) => {
    let targetPermission = record;
    if (record) {
      targetPermission = await hydratePermissionForm(record);
    }
    setEditingPermission(targetPermission);
    if (targetPermission) {
      permissionForm.setFieldsValue({
        name: targetPermission.name,
        display_name: targetPermission.display_name,
        description: targetPermission.description,
        resource: targetPermission.resource,
        action: targetPermission.action,
        is_active: Number(targetPermission.is_active) === 1,
      });
    } else {
      permissionForm.resetFields();
      setSelectedCatalogModule(undefined);
      setCatalogActions([]);
      permissionForm.setFieldsValue({
        is_active: true,
      });
    }
    setPermissionModalVisible(true);
  };

  const handleRoleStatusSwitch = async (checked) => {
    roleForm.setFieldsValue({ is_active: checked });
    if (!editingRole) return;
    const roleId = editingRole.id || editingRole.role_id;
    setRoleStatusUpdating(true);
    try {
      await toggleRoleStatus(roleId, checked ? 'active' : 'inactive');
      notification.success({
        message: checked ? 'Role activated' : 'Role deactivated',
        placement: 'topLeft',
      });
      setEditingRole((prev) => (prev ? { ...prev, is_active: checked ? 1 : 0 } : prev));
      await loadRoles();
    } catch (error) {
      roleForm.setFieldsValue({ is_active: !checked });
      notification.error({
        message: 'Failed to update role status',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    } finally {
      setRoleStatusUpdating(false);
    }
  };

  const handleRoleSystemSwitch = async (checked) => {
    roleForm.setFieldsValue({ is_system_role: checked });
    if (!editingRole) return;
    const roleId = editingRole.id || editingRole.role_id;
    setRoleSystemUpdating(true);
    try {
      await toggleRoleSystemFlag(roleId, checked);
      notification.success({
        message: checked ? 'Role marked as system' : 'Role marked as custom',
        placement: 'topLeft',
      });
      setEditingRole((prev) => (prev ? { ...prev, is_system_role: checked ? 1 : 0 } : prev));
      await loadRoles();
    } catch (error) {
      roleForm.setFieldsValue({ is_system_role: !checked });
      notification.error({
        message: 'Failed to update system flag',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    } finally {
      setRoleSystemUpdating(false);
    }
  };

  const handlePermissionStatusSwitch = async (checked) => {
    permissionForm.setFieldsValue({ is_active: checked });
    if (!editingPermission) return;
    const permissionId = editingPermission.id || editingPermission.permission_id;
    setPermissionStatusUpdating(true);
    try {
      await togglePermissionStatus(permissionId, checked ? 'active' : 'inactive');
      notification.success({
        message: checked ? 'Permission activated' : 'Permission deactivated',
        placement: 'topLeft',
      });
      setEditingPermission((prev) => (prev ? { ...prev, is_active: checked ? 1 : 0 } : prev));
      await loadPermissions();
    } catch (error) {
      permissionForm.setFieldsValue({ is_active: !checked });
      notification.error({
        message: 'Failed to update permission status',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    } finally {
      setPermissionStatusUpdating(false);
    }
  };

  // ----- Permission catalog -------------------------------------------------
  // The New-permission form picks a module and then one of that module's
  // actions, instead of typing `resource` and `action` freehand. Only actions
  // with no permission yet are offered, so the same capability cannot get a
  // second, differently-spelled permission. Backed by /v2/rbac/permission-modules.
  const loadCatalogModules = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const body = await listPermissionModules();
      const data = body?.data || {};
      setCatalogEnabled(data.enabled !== false);
      setCatalogModules(Array.isArray(data.modules) ? data.modules : []);
    } catch {
      // Older backend without the catalog: fall back to the free-text fields.
      setCatalogEnabled(false);
      setCatalogModules([]);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const loadCatalogActions = useCallback(async (moduleKey) => {
    if (!moduleKey) {
      setCatalogActions([]);
      return;
    }
    setCatalogActionsLoading(true);
    try {
      // 'unused' is the whole point: an action that already has a permission
      // must not be offered again.
      const body = await listModuleActions(moduleKey, 'unused');
      const data = body?.data || {};
      setCatalogActions(Array.isArray(data.actions) ? data.actions : []);
    } catch {
      setCatalogActions([]);
    } finally {
      setCatalogActionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalogModules();
  }, [loadCatalogModules]);

  const handleCatalogModuleChange = (moduleKey) => {
    setSelectedCatalogModule(moduleKey);
    permissionForm.setFieldsValue({ catalog_action: undefined, name: undefined, resource: undefined, action: undefined });
    loadCatalogActions(moduleKey);
  };

  const handleCatalogActionChange = (actionId) => {
    const chosen = catalogActions.find((a) => a.id === actionId);
    if (!chosen) return;
    // The slug is the catalog's, not the admin's — this is what keeps
    // facilities.deactivate from also becoming facilities.disable next month.
    permissionForm.setFieldsValue({
      name: chosen.permission_slug,
      resource: chosen.resource,
      action: chosen.action,
      display_name: permissionForm.getFieldValue('display_name') || chosen.label,
    });
  };

  // ----- Extending the catalog ----------------------------------------------
  // Before this, the catalog could only grow by editing
  // Aman\Seeders\PermissionCatalogDefaults and deploying, so an admin who
  // needed a capability the catalog did not describe had nowhere to go — the
  // New-permission form would simply not offer it. These two forms close that
  // loop. Validation mirrors the server (see catalogFormHelpers); the server
  // still decides.

  /**
   * The whole catalog, for the clash check.
   *
   * Deliberately not just the chosen module's actions: the server rejects a
   * resource/action pair catalogued under ANY module, because the pair is what
   * a permission is made of. Checking only the current module would let the UI
   * accept a draft the server then refuses.
   */
  const loadAllCatalogActions = useCallback(async () => {
    try {
      const body = await listModuleActions('all', 'all');
      const data = body?.data || {};
      setAllCatalogActions(Array.isArray(data.actions) ? data.actions : []);
    } catch {
      // A failed prefetch only costs the early warning — the server's 409
      // still stops a duplicate.
      setAllCatalogActions([]);
    }
  }, []);

  const reportDraftErrors = (errors, form) => {
    form.setFields(
      Object.entries(errors).map(([name, message]) => ({ name, errors: [message] }))
    );
  };

  const openModuleDraft = () => {
    moduleDraftForm.resetFields();
    setModuleDraftOpen(true);
  };

  const handleCreateModule = async () => {
    const draft = moduleDraftForm.getFieldsValue();
    const { ok, errors } = validateModuleDraft(draft, catalogModules);
    if (!ok) {
      reportDraftErrors(errors, moduleDraftForm);
      return;
    }
    setCatalogSaving(true);
    try {
      await createPermissionModule({
        module_key: String(draft.module_key).trim().toLowerCase(),
        label: String(draft.label).trim(),
        description: draft.description || null,
        sort_order: draft.sort_order ?? 999,
      });
      notification.success({ message: `Module "${draft.label}" added to the catalog`, placement: 'topLeft' });
      setModuleDraftOpen(false);
      await loadCatalogModules();
      const moduleKey = String(draft.module_key).trim().toLowerCase();
      setSelectedCatalogModule(moduleKey);
      permissionForm.setFieldsValue({ catalog_module: moduleKey, catalog_action: undefined });
      await loadCatalogActions(moduleKey);
      // A module starts empty, so the permission still has no action to point
      // at. Going straight on to the action form is the only useful next step.
      openActionDraft(moduleKey);
    } catch (error) {
      notification.error({
        message: 'Failed to add module',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    } finally {
      setCatalogSaving(false);
    }
  };

  const openActionDraft = (moduleKeyOverride) => {
    const moduleKey = moduleKeyOverride || selectedCatalogModule;
    if (!moduleKey) return;
    loadAllCatalogActions();
    actionDraftForm.resetFields();
    actionDraftForm.setFieldsValue({
      resource: suggestResourceForModule(moduleKey, catalogActions),
    });
    setActionDraftOpen(true);
  };

  const handleCreateAction = async () => {
    const moduleKey = selectedCatalogModule;
    const draft = actionDraftForm.getFieldsValue();
    const { ok, errors } = validateActionDraft(draft, allCatalogActions);
    if (!ok) {
      reportDraftErrors(errors, actionDraftForm);
      return;
    }
    const resource = String(draft.resource).trim().toLowerCase();
    const action = String(draft.action).trim().toLowerCase();
    setCatalogSaving(true);
    try {
      const body = await createModuleAction(moduleKey, {
        label: String(draft.label).trim(),
        description: draft.description || null,
        resource,
        action,
        action_key: action,
        sort_order: draft.sort_order ?? 999,
      });
      notification.success({ message: `${resource}.${action} added to the catalog`, placement: 'topLeft' });
      setActionDraftOpen(false);
      await loadCatalogModules();
      await loadCatalogActions(moduleKey);
      loadAllCatalogActions();
      // Select what was just created, so the admin lands back on a form that
      // is one click from done rather than one they have to re-navigate.
      const newId = body?.data?.id;
      permissionForm.setFieldsValue({
        catalog_action: newId ?? undefined,
        name: `${resource}.${action}`,
        resource,
        action,
        display_name: permissionForm.getFieldValue('display_name') || String(draft.label).trim(),
      });
    } catch (error) {
      notification.error({
        message: 'Failed to add action',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    } finally {
      setCatalogSaving(false);
    }
  };

  const handleSubmitPermission = async () => {
    try {
      const values = await permissionForm.validateFields();
      const payload = {
        name: values.name,
        display_name: values.display_name,
        description: values.description,
        resource: values.resource,
        action: values.action,
      };
      if (editingPermission) {
        const permissionId = editingPermission.id || editingPermission.permission_id;
        await updatePermissionRecord(permissionId, payload);
        const desiredStatus = values.is_active ? 'active' : 'inactive';
        const currentStatus = Number(editingPermission.is_active) === 1 ? 'active' : 'inactive';
        if (desiredStatus !== currentStatus) {
          await togglePermissionStatus(permissionId, desiredStatus);
        }
        notification.success({ message: 'Permission updated', placement: 'topLeft' });
      } else {
        await createPermissionRecord(payload);
        notification.success({ message: 'Permission created', placement: 'topLeft' });
        // The action just consumed is no longer available — refresh the counts.
        loadCatalogModules();
        if (selectedCatalogModule) loadCatalogActions(selectedCatalogModule);
      }
      setPermissionModalVisible(false);
      setEditingPermission(null);
      permissionForm.resetFields();
      loadPermissions();
    } catch (error) {
      if (error?.errorFields) return;
      notification.error({
        message: editingPermission ? 'Failed to update permission' : 'Failed to create permission',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    }
  };

  const handleDeletePermission = async (record) => {
    try {
      await deletePermissionRecord(record.id || record.permission_id);
      notification.success({ message: 'Permission deleted', placement: 'topLeft' });
      loadPermissions();
    } catch (error) {
      notification.error({
        message: 'Failed to delete permission',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    }
  };

  const openPermissionDrawer = async (role) => {
    setSelectedRole(role);
    setPermissionDrawerVisible(true);
    setPermissionSyncLoading(true);
    try {
      const roleResponse = await getRoleDetails(role.id || role.role_id);
      const rolePayload = roleResponse?.data || roleResponse;
      const attachedPermissions = rolePayload?.permissions || role.permissions || [];
      const attachedKeys = attachedPermissions.map((permission) =>
        String(permission.id ?? permission.permission_id)
      );

      // The editor groups by catalog module, so both halves of the catalog
      // have to be in hand before it renders — without them every permission
      // falls into "Not in the catalog".
      await Promise.all([loadCatalogPermissions(), loadCatalogModules(), loadAllCatalogActions()]);
      setAssignedPermissionKeys(attachedKeys);
      setInitialPermissionKeys(attachedKeys);
    } catch (error) {
      notification.error({
        message: 'Unable to load role permissions',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
      setPermissionDrawerVisible(false);
    } finally {
      setPermissionSyncLoading(false);
    }
  };

  const handleSyncRolePermissions = async () => {
    if (!selectedRole) return;
    setPermissionSyncLoading(true);
    try {
      const roleId = selectedRole.id || selectedRole.role_id;
      const toAdd = assignedPermissionKeys.filter((id) => !initialPermissionKeys.includes(id));
      const toRemove = initialPermissionKeys.filter((id) => !assignedPermissionKeys.includes(id));

      if (toAdd.length) {
        await assignPermissionsToRole(roleId, {
          permission_ids: toAdd.map((id) => Number(id)),
        });
      }

      if (toRemove.length) {
        await Promise.all(
          toRemove.map((permissionId) =>
            removePermissionFromRole(roleId, permissionId)
          )
        );
      }

      notification.success({
        message: 'Role permissions updated',
        placement: 'topLeft',
      });

      setPermissionDrawerVisible(false);
      setSelectedRole(null);
      setAssignedPermissionKeys([]);
      setInitialPermissionKeys([]);
      loadRoles();
      try {
        await refreshProfile();
      } catch (error) {
        console.error('Failed to refresh session after role permission sync:', error);
      }
    } catch (error) {
      notification.error({
        message: 'Failed to update role permissions',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    } finally {
      setPermissionSyncLoading(false);
    }
  };

  const loadVerification = useCallback(async () => {
    setVerificationLoading(true);
    try {
      const response = await verifyRbacSystem();
      if (response?.error === 0 && response?.data) {
        setVerificationData(response.data);
        
        // Determine health status
        const { permissions, roles, role_permission_mappings, user_role_assignments } = response.data;
        const hasPermissions = permissions.active > 0;
        const hasRoles = roles.active > 0;
        const hasMappings = role_permission_mappings.total > 0;
        const hasAssignments = Object.values(user_role_assignments).some(a => a.active > 0);
        
        if (hasPermissions && hasRoles && hasMappings && hasAssignments) {
          setVerificationHealth('healthy');
        } else if (hasPermissions && hasRoles && hasMappings) {
          setVerificationHealth('warning'); // No user assignments yet
        } else {
          setVerificationHealth('error'); // Missing core data
        }
      } else {
        setVerificationHealth('error');
        notification.error({
          message: 'Verification failed',
          description: response?.message || 'Unable to verify RBAC system',
          placement: 'topLeft',
        });
      }
    } catch (error) {
      console.error('Failed to load verification:', error);
      setVerificationHealth('error');
      notification.error({
        message: 'Verification failed',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    } finally {
      setVerificationLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManageRBAC && activeTab === 'verification') {
      loadVerification();
    }
  }, [canManageRBAC, activeTab, loadVerification]);

  const handleRunSeeder = async () => {
    Modal.confirm({
      title: 'Run RBAC Seeder?',
      icon: <ThunderboltOutlined style={{ color: '#faad14' }} />,
      content:
        'This operation is idempotent but will rerun migrations for legacy users. Proceed only if infrastructure requests it.',
      okText: 'Run Seeder',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setSeederLoading(true);
        try {
          const response = await runRbacSeeder({ confirm: 'RBAC_SEED_NOW' });
          setSeederResult(response?.data || response);
          notification.success({
            message: response?.message || 'RBAC seeding complete',
            placement: 'topLeft',
          });
          // Refresh verification after seeding
          if (activeTab === 'verification') {
            await loadVerification();
          }
        } catch (error) {
          notification.error({
            message: 'Seeder failed',
            description: error?.response?.data?.message || error?.message,
            placement: 'topLeft',
          });
        } finally {
          setSeederLoading(false);
        }
      },
    });
  };

  const handleRunCleanup = async () => {
    if (cleanupConfirmText !== 'RBAC_CLEAN_NOW') {
      notification.error({
        message: 'Invalid Confirmation',
        description: 'You must type "RBAC_CLEAN_NOW" exactly to confirm.',
        placement: 'topLeft',
      });
      return;
    }

    setCleanupLoading(true);
    try {
      const response = await cleanupRbacSystem(cleanupScope);
      if (response?.error === 0) {
        setCleanupResult(response?.data || response);
        notification.success({
          message: response?.message || 'RBAC cleanup complete',
          placement: 'topLeft',
          duration: 5,
        });
        setCleanupModalVisible(false);
        setCleanupConfirmText('');
        // Refresh verification after cleanup
        if (activeTab === 'verification') {
          await loadVerification();
        }
        // Refresh roles and permissions if they were affected
        if (cleanupScope === 'mappings' || cleanupScope === 'all') {
          await loadRoles();
          await loadPermissions();
        }
      } else {
        notification.error({
          message: 'Cleanup failed',
          description: response?.message || 'Unable to clean RBAC data',
          placement: 'topLeft',
        });
      }
    } catch (error) {
      notification.error({
        message: 'Cleanup failed',
        description: error?.response?.data?.message || error?.message,
        placement: 'topLeft',
      });
    } finally {
      setCleanupLoading(false);
    }
  };

  const hasActiveRoleFilters = Boolean(roleSearch || roleStatus);
  const hasActivePermissionFilters = Boolean(permissionSearch || permissionResource || permissionStatus);

  const clearRoleFilters = () => {
    setRoleSearch('');
    setRoleUserType('staff');
    setRoleStatus(undefined);
  };

  const clearPermissionFilters = () => {
    setPermissionSearch('');
    setPermissionResource(undefined);
    setPermissionStatus(undefined);
  };

  // The Catalog tab's "outside the catalog" list needs every permission, not
  // the page the Permissions table happens to be showing.
  useEffect(() => {
    if (activeTab === 'catalog' && catalogPermissions.length === 0) {
      loadCatalogPermissions();
    }
    // The Permissions table's Module column reads the same catalog.
    if ((activeTab === 'catalog' || activeTab === 'permissions') && allCatalogActions.length === 0) {
      loadAllCatalogActions();
    }
  }, [activeTab, catalogPermissions.length, allCatalogActions.length, loadCatalogPermissions, loadAllCatalogActions]);

  const refreshActiveTab = () => {
    if (activeTab === 'roles') {
      loadRoles();
    } else if (activeTab === 'permissions') {
      loadPermissions();
      loadAllCatalogActions();
    } else if (activeTab === 'catalog') {
      loadCatalogPermissions();
    } else if (activeTab === 'glossary') {
      loadGlossary();
    } else if (activeTab === 'verification') {
      loadVerification();
    }
  };

  const activeRolesOnPage = useMemo(
    () => roles.filter((role) => Number(role.is_active) === 1).length,
    [roles]
  );

  const systemRolesOnPage = useMemo(
    () => roles.filter((role) => Boolean(role.is_system_role)).length,
    [roles]
  );

  const heroMetrics = useMemo(() => {
    if (activeTab === 'roles') {
      return [
        {
          key: 'total',
          title: 'Total roles',
          value: rolePagination.total,
          color: '#3497D3',
          icon: <SafetyCertificateOutlined />,
          subtitle: hasActiveRoleFilters ? 'Matching filters' : 'In system',
        },
        {
          key: 'page',
          title: 'On this page',
          value: roles.length,
          color: '#722ed1',
          icon: <UnorderedListOutlined />,
          subtitle: `Page ${rolePagination.current}`,
        },
        {
          key: 'active',
          title: 'Active',
          value: activeRolesOnPage,
          color: '#49B870',
          icon: <CheckCircleOutlined />,
          subtitle: 'On this page',
        },
        {
          key: 'system',
          title: 'System',
          value: systemRolesOnPage,
          color: '#faad14',
          icon: <ThunderboltOutlined />,
          subtitle: 'On this page',
        },
      ].filter((metric) => hasMetricValue(metric.value) || metric.key === 'page');
    }

    if (activeTab === 'permissions') {
      const activeCount = permissions.filter((permission) => Number(permission.is_active) === 1).length;
      const resourceCount = new Set(permissions.map((permission) => permission.resource).filter(Boolean)).size;

      return [
        {
          key: 'total',
          title: 'Permissions',
          value: permissions.length,
          color: '#3497D3',
          icon: <KeyOutlined />,
          subtitle: hasActivePermissionFilters ? 'Matching filters' : 'Loaded',
        },
        {
          key: 'active',
          title: 'Active',
          value: activeCount,
          color: '#49B870',
          icon: <CheckCircleOutlined />,
          subtitle: 'In list',
        },
        {
          key: 'resources',
          title: 'Resources',
          value: resourceCount,
          color: '#722ed1',
          icon: <ApiOutlined />,
          subtitle: 'Unique',
        },
        {
          key: 'inactive',
          title: 'Inactive',
          value: Math.max(0, permissions.length - activeCount),
          color: '#94a3b8',
          icon: <WarningOutlined />,
          subtitle: 'In list',
        },
      ].filter((metric) => hasMetricValue(metric.value));
    }

    if (activeTab === 'verification' && verificationData) {
      const usersWithRoles = Object.values(verificationData.user_role_assignments).reduce(
        (sum, assignment) => sum + Number(assignment.unique_users || 0),
        0
      );

      return [
        {
          key: 'permissions',
          title: 'Permissions',
          value: verificationData.permissions.active,
          color: '#3497D3',
          icon: <KeyOutlined />,
          subtitle: `${verificationData.permissions.total} total`,
        },
        {
          key: 'roles',
          title: 'Roles',
          value: verificationData.roles.active,
          color: '#49B870',
          icon: <SafetyCertificateOutlined />,
          subtitle: `${verificationData.roles.total} total`,
        },
        {
          key: 'mappings',
          title: 'Mappings',
          value: verificationData.role_permission_mappings.total,
          color: '#722ed1',
          icon: <ApiOutlined />,
          subtitle: 'Role-permission',
        },
        {
          key: 'users',
          title: 'Users with roles',
          value: usersWithRoles,
          color: '#faad14',
          icon: <TeamOutlined />,
          subtitle: 'Across types',
        },
      ];
    }

    return [];
  }, [
    activeTab,
    roles.length,
    rolePagination.total,
    rolePagination.current,
    activeRolesOnPage,
    systemRolesOnPage,
    permissions,
    verificationData,
    hasActiveRoleFilters,
    hasActivePermissionFilters,
  ]);

  const heroLoading =
    (activeTab === 'roles' && roleLoading && !roles.length) ||
    (activeTab === 'permissions' && permissionLoading && !permissions.length) ||
    (activeTab === 'verification' && verificationLoading && !verificationData);

  const roleColumns = [
    {
      title: 'Display Name',
      dataIndex: 'display_name',
      key: 'display_name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.name}
          </Text>
        </Space>
      ),
    },
    {
      title: 'User Type',
      dataIndex: 'user_type',
      key: 'user_type',
      render: (value) => <Tag color="blue">{value || 'n/a'}</Tag>,
    },
    {
      title: 'Landing',
      dataIndex: 'home_module',
      key: 'home_module',
      render: (value, record) => {
        if (!value) return <Text type="secondary">Default order</Text>;
        const mod = getAccessModule(value);
        const priority = Number(record.home_priority || 0);
        return (
          <Space direction="vertical" size={0}>
            <Text>{mod?.label || value}</Text>
            {priority ? (
              <Text type="secondary" style={{ fontSize: 12 }}>Priority {priority}</Text>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: 'System Role',
      dataIndex: 'is_system_role',
      key: 'is_system_role',
      render: (value) =>
        value ? <Tag color="red">System</Tag> : <Tag color="green">Custom</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (value) =>
        Number(value) === 1 ? (
          <Badge status="success" text="Active" />
        ) : (
          <Badge status="default" text="Inactive" />
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button type="link" onClick={() => openPermissionDrawer(record)} icon={<KeyOutlined />}>
            Permissions
          </Button>
          <Button type="link" onClick={() => handleOpenRoleModal(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete role"
            description="Are you sure you want to delete this role?"
            onConfirm={() => handleDeleteRole(record)}
            okType="danger"
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // slug -> catalog module label, so the flat permission list can be read
  // structurally. Without it the table shows resource and action but never
  // which product area owns them.
  const moduleLabelBySlug = useMemo(() => {
    const map = new Map();
    (allCatalogActions || []).forEach((a) => {
      const slug = a?.permission_slug || (a?.resource && a?.action ? `${a.resource}.${a.action}` : '');
      if (slug) map.set(slug, a.module_label || a.module_key);
    });
    return map;
  }, [allCatalogActions]);

  const permissionColumns = [
    {
      title: 'Display Name',
      dataIndex: 'display_name',
      key: 'display_name',
      width: 260,
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
            {record.name}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Module',
      key: 'catalog_module',
      width: 150,
      render: (_, record) => {
        const label = moduleLabelBySlug.get(String(record?.name || ''));
        if (label) return <Tag color="blue">{label}</Tag>;
        // Wildcards sit outside the catalog on purpose; anything else here is
        // drift, and the Catalog tab lists it in full.
        const isWildcard = String(record?.name || '').includes('*');
        return (
          <Tooltip title={isWildcard
            ? 'Wildcards are deliberately outside the catalog.'
            : 'No catalog action describes this permission — see the Catalog tab.'}>
            <Tag color={isWildcard ? 'default' : 'orange'}>{isWildcard ? 'Wildcard' : 'Uncatalogued'}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Resource',
      dataIndex: 'resource',
      key: 'resource',
      width: 130,
      render: (value) => <Tag color="geekblue">{value || 'general'}</Tag>,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 130,
      render: (value) => <Tag color="purple">{value || 'n/a'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'permission_status',
      width: 100,
      render: (value) =>
        Number(value) === 1 ? (
          <Badge status="success" text="Active" />
        ) : (
          <Badge status="default" text="Inactive" />
        ),
    },
    {
      title: 'Affects',
      key: 'affects',
      render: (_, record) => <AffectsCell modules={modulesForPermission(record.name)} />,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" onClick={() => handleOpenPermissionModal(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete permission"
            description="Are you sure you want to delete this permission?"
            onConfirm={() => handleDeletePermission(record)}
            okType="danger"
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!canManageRBAC) {
    return (
      <div className="rbac-access-denied">
        <Result
          status="403"
          title="Access Denied"
          subTitle="You need the admin.role_management permission to view this page."
        />
      </div>
    );
  }

  const verificationTabLabel = (
    <Space size={4}>
      <span>Verification</span>
      {verificationHealth === 'healthy' && <CheckCircleOutlined style={{ color: '#49B870' }} />}
      {verificationHealth === 'warning' && <WarningOutlined style={{ color: '#faad14' }} />}
      {verificationHealth === 'error' && <WarningOutlined style={{ color: '#ff4d4f' }} />}
    </Space>
  );

  const rbacTabBar = (
    <Segmented
      value={activeTab}
      onChange={setActiveTab}
      options={[
        { label: 'Roles', value: 'roles' },
        { label: 'Permissions', value: 'permissions' },
        { label: 'Catalog', value: 'catalog' },
        // Renamed from "Modules": it lists SCREENS (routes + who may open
        // them), while the catalog above owns the modules that group
        // capabilities. One word for two axes was the main reason the
        // module -> action -> role chain looked like it did not exist.
        { label: 'Screens', value: 'modules' },
        { label: 'Glossary', value: 'glossary' },
        { label: verificationTabLabel, value: 'verification' },
        { label: 'Seeder & Cleanup', value: 'seeder' },
      ]}
    />
  );

  return (
    <div className="rbac-dashboard">
      <div className="dashboard-page-stack">
        <header className="dash-masthead">
          <div className="dash-masthead__lead">
            <h1 className="dash-masthead__title">RBAC Administration</h1>
            <p className="dash-masthead__meta">
              Manage roles, permissions, modules, and seeder operations
            </p>
          </div>
        </header>

        {heroMetrics.length > 0 || heroLoading ? (
          <section className="rbac-overview dash-overview dash-overview--compact" aria-label="RBAC summary">
            <DashboardHero metrics={heroMetrics} loading={heroLoading} />
          </section>
        ) : null}

        <div className="dash-tab-strip" role="tablist" aria-label="RBAC sections">
          {rbacTabBar}
        </div>

        {activeTab === 'roles' ? (
          <DashboardSection
            id="rbac-roles"
            icon={<SafetyCertificateOutlined />}
            title="Roles"
            subtitle={
              roleLoading
                ? 'Loading…'
                : `${roles.length} of ${rolePagination.total} · page ${rolePagination.current}`
            }
            collapsible={false}
            variant="content"
            action={(
              <div className="rbac-toolbar rbac-toolbar--inline">
                <Input
                  className="rbac-toolbar__search"
                  size="small"
                  placeholder="Search roles…"
                  prefix={<SearchOutlined />}
                  value={roleSearch}
                  onChange={(e) => setRoleSearch(e.target.value)}
                  allowClear
                />
                <Segmented
                  size="small"
                  value={roleUserType}
                  onChange={setRoleUserType}
                  options={roleTypeSegments}
                />
                <Select
                  className="rbac-toolbar__select"
                  size="small"
                  placeholder="Status"
                  allowClear
                  value={roleStatus}
                  onChange={setRoleStatus}
                  options={statusFilterOptions}
                />
                <Button size="small" onClick={clearRoleFilters} disabled={!hasActiveRoleFilters}>
                  Clear
                </Button>
                <Button size="small" icon={<ReloadOutlined />} onClick={refreshActiveTab} loading={roleLoading}>
                  Refresh
                </Button>
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => handleOpenRoleModal()}>
                  New role
                </Button>
              </div>
            )}
          >
            <div className="rbac-table-wrap">
              <Table
                rowKey={(record) => record.id || record.role_id || record.uuid}
                loading={roleLoading}
                columns={roleColumns}
                dataSource={roles}
                size="small"
                pagination={{
                  current: rolePagination.current,
                  pageSize: rolePagination.pageSize,
                  total: rolePagination.total,
                  showSizeChanger: true,
                }}
                onChange={(pagination) => {
                  loadRoles({ page: pagination.current, limit: pagination.pageSize });
                }}
              />
            </div>
          </DashboardSection>
        ) : null}

        {activeTab === 'permissions' ? (
          <DashboardSection
            id="rbac-permissions"
            icon={<KeyOutlined />}
            title="Permissions"
            subtitle={
              permissionLoading
                ? 'Loading…'
                : `${permissions.length} permission${permissions.length === 1 ? '' : 's'} in this view`
            }
            collapsible={false}
            variant="content"
            action={(
              <div className="rbac-toolbar rbac-toolbar--inline">
                <Input
                  className="rbac-toolbar__search"
                  size="small"
                  placeholder="Search permissions…"
                  prefix={<SearchOutlined />}
                  value={permissionSearch}
                  onChange={(e) => setPermissionSearch(e.target.value)}
                  allowClear
                />
                <Input
                  className="rbac-toolbar__select"
                  size="small"
                  placeholder="Resource"
                  allowClear
                  value={permissionResource}
                  onChange={(e) => setPermissionResource(e.target.value || undefined)}
                />
                <Select
                  className="rbac-toolbar__select"
                  size="small"
                  placeholder="Status"
                  allowClear
                  value={permissionStatus}
                  onChange={setPermissionStatus}
                  options={statusFilterOptions}
                />
                <Button size="small" onClick={clearPermissionFilters} disabled={!hasActivePermissionFilters}>
                  Clear
                </Button>
                <Button size="small" icon={<ReloadOutlined />} onClick={refreshActiveTab} loading={permissionLoading}>
                  Refresh
                </Button>
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => handleOpenPermissionModal()}>
                  New permission
                </Button>
              </div>
            )}
          >
            <div className="rbac-table-wrap">
              <Table
                rowKey={(record) => record.id || record.permission_id}
                loading={permissionLoading}
                columns={permissionColumns}
                dataSource={permissions}
                size="small"
                pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `${total} permissions` }}
                scroll={{ x: 1000 }}
              />
            </div>
          </DashboardSection>
        ) : null}

        {activeTab === 'catalog' ? (
          <CatalogTab permissions={catalogPermissions} />
        ) : null}

        {activeTab === 'modules' ? (
          <AccessModulesTab onCatalogChange={refreshAccessCatalog} />
        ) : null}

        {activeTab === 'glossary' ? (
          <DashboardSection
            id="rbac-glossary"
            icon={<BookOutlined />}
            title="Glossary"
            subtitle="What each permission unlocks, and which screens each role can open"
            collapsible={false}
            variant="content"
            action={(
              <Button size="small" icon={<ReloadOutlined />} onClick={refreshActiveTab} loading={glossaryLoading}>
                Refresh
              </Button>
            )}
          >
            {glossaryLoading && !glossaryRoles.length && !catalogPermissions.length ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                <Spin />
              </div>
            ) : (
              <RbacGlossaryTab permissions={catalogPermissions} roles={glossaryRoles} />
            )}
          </DashboardSection>
        ) : null}

        {activeTab === 'verification' ? (
          <DashboardSection
            id="rbac-verification"
            icon={<CheckCircleOutlined />}
            title="System verification"
            subtitle="Health check for permissions, roles, mappings, and assignments"
            collapsible={false}
            variant="content"
            action={(
              <Button size="small" icon={<ReloadOutlined />} onClick={loadVerification} loading={verificationLoading}>
                Refresh
              </Button>
            )}
          >
            {verificationLoading ? (
              <div className="rbac-loading">
                <Spin size="large" />
              </div>
            ) : verificationData ? (
              <>
                <Alert
                  className="rbac-verification-alert"
                  type={verificationHealth === 'healthy' ? 'success' : verificationHealth === 'warning' ? 'warning' : 'error'}
                  message={
                    verificationHealth === 'healthy'
                      ? 'RBAC system is healthy'
                      : verificationHealth === 'warning'
                        ? 'RBAC system has warnings'
                        : 'RBAC system has issues'
                  }
                  description={
                    verificationHealth === 'healthy'
                      ? 'All core components are present and active.'
                      : verificationHealth === 'warning'
                        ? 'Core components exist but user assignments may be missing.'
                        : 'Some core components are missing or inactive.'
                  }
                  showIcon
                />

                <DashboardSection
                  id="rbac-verification-resources"
                  icon={<ApiOutlined />}
                  title="Permissions by resource"
                  collapsible={false}
                  variant="content"
                >
                  <div className="rbac-table-wrap">
                    <Table
                      dataSource={Object.entries(verificationData.permissions.by_resource).map(([resource, count]) => ({
                        key: resource,
                        resource,
                        count,
                      }))}
                      columns={[
                        { title: 'Resource', dataIndex: 'resource', key: 'resource', render: (text) => <Tag color="geekblue">{text}</Tag> },
                        { title: 'Count', dataIndex: 'count', key: 'count' },
                      ]}
                      pagination={false}
                      size="small"
                    />
                  </div>
                </DashboardSection>

                <DashboardSection
                  id="rbac-verification-roles"
                  icon={<TeamOutlined />}
                  title="Roles by user type"
                  collapsible={false}
                  variant="content"
                >
                  <div className="rbac-table-wrap">
                    <Table
                      dataSource={Object.entries(verificationData.roles.by_user_type).map(([type, count]) => ({
                        key: type,
                        type,
                        count,
                      }))}
                      columns={[
                        { title: 'User type', dataIndex: 'type', key: 'type', render: (text) => <Tag color="blue">{text}</Tag> },
                        { title: 'Count', dataIndex: 'count', key: 'count' },
                      ]}
                      pagination={false}
                      size="small"
                    />
                  </div>
                </DashboardSection>

                <DashboardSection
                  id="rbac-verification-assignments"
                  icon={<SafetyCertificateOutlined />}
                  title="User role assignments"
                  collapsible={false}
                  variant="content"
                >
                  <div className="rbac-table-wrap">
                    <Table
                      dataSource={Object.entries(verificationData.user_role_assignments).map(([type, stats]) => ({
                        key: type,
                        type,
                        ...stats,
                      }))}
                      columns={[
                        { title: 'User type', dataIndex: 'type', key: 'type', render: (text) => <Tag color="cyan">{text}</Tag> },
                        { title: 'Total assignments', dataIndex: 'total', key: 'total' },
                        { title: 'Active assignments', dataIndex: 'active', key: 'active' },
                        { title: 'Unique users', dataIndex: 'unique_users', key: 'unique_users' },
                      ]}
                      pagination={false}
                      size="small"
                    />
                  </div>
                </DashboardSection>
              </>
            ) : (
              <Empty description="No verification data available. Try refreshing." />
            )}
          </DashboardSection>
        ) : null}

        {activeTab === 'seeder' ? (
          <>
            <DashboardSection
              id="rbac-seeder"
              icon={<ThunderboltOutlined />}
              title="RBAC seeder"
              subtitle="Temporary migration endpoint — use only when infrastructure requests it"
              collapsible={false}
              variant="content"
              action={(
                <Button
                  type="primary"
                  danger
                  size="small"
                  icon={<ThunderboltOutlined />}
                  loading={seederLoading}
                  onClick={handleRunSeeder}
                >
                  Run seeder
                </Button>
              )}
            >
              <Alert
                type="warning"
                message="Temporary RBAC seeder"
                description="This endpoint reruns RBAC migrations. Only run it when infrastructure explicitly requests it."
                showIcon
              />
              {seederResult ? (
                <Descriptions className="rbac-result-descriptions" title="Last seeder result" bordered size="small" column={1}>
                  {Object.entries(seederResult).map(([key, value]) => (
                    <Descriptions.Item key={key} label={key}>
                      {String(value)}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              ) : null}
            </DashboardSection>

            <DashboardSection
              id="rbac-cleanup"
              icon={<SafetyCertificateOutlined />}
              title="RBAC cleanup"
              subtitle="Remove assignments, mappings, or all RBAC data before re-seeding"
              collapsible={false}
              variant="content"
              action={(
                <Button
                  type="primary"
                  danger
                  size="small"
                  icon={<SafetyCertificateOutlined />}
                  onClick={() => setCleanupModalVisible(true)}
                >
                  Clean RBAC data
                </Button>
              )}
            >
              <Alert
                type="error"
                message="Destructive operation"
                description="This will remove RBAC data. Use before re-seeding to fix incorrect assignments or mappings."
                showIcon
              />
              {cleanupResult ? (
                <Descriptions className="rbac-result-descriptions" title="Last cleanup result" bordered size="small" column={1}>
                  {Object.entries(cleanupResult).map(([key, value]) => (
                    <Descriptions.Item key={key} label={key}>
                      {String(value)}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              ) : null}
            </DashboardSection>
          </>
        ) : null}
      </div>

      <Modal
        title={editingRole ? 'Edit Role' : 'Create Role'}
        open={roleModalVisible}
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        onCancel={() => {
          setRoleModalVisible(false);
          setEditingRole(null);
          roleForm.resetFields();
        }}
        okText={editingRole ? 'Update Role' : 'Create Role'}
        onOk={handleSubmitRole}
      >
        <Form layout="vertical" form={roleForm}>
          <Form.Item
            label="Display Name"
            name="display_name"
            rules={[{ required: true, message: 'Display name is required' }]}
          >
            <Input placeholder="e.g. Claims Manager" />
          </Form.Item>
          <Form.Item
            label="Name (slug)"
            name="name"
            rules={[{ required: true, message: 'Role name is required' }]}
          >
            <Input placeholder="e.g. claims_manager" disabled={Boolean(editingRole)} />
          </Form.Item>
          <Form.Item
            label="User Type"
            name="user_type"
            rules={[{ required: true, message: 'Select a user type' }]}
          >
            <Select options={userTypeOptions} placeholder="Select user type" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} placeholder="Optional description" />
          </Form.Item>
          <Form.Item
            label="Landing page"
            name="home_module"
            tooltip="Where people with this role go after login. If they have several roles, the highest priority landing they can still open wins."
          >
            <Select
              allowClear
              placeholder="Default (claims → preauth → dashboard)"
              options={careAdminHomeSelectOptions()}
            />
          </Form.Item>
          <Form.Item
            label="Landing priority"
            name="home_priority"
            tooltip="Used only when the same person has more than one role with a landing page. Higher number wins."
          >
            <InputNumber min={0} max={1000} style={{ width: '100%' }} />
          </Form.Item>
          {editingRole && (
            <>
              <Divider />
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Advanced Controls
              </Text>
              <Form.Item
                label="Status"
                name="is_active"
                valuePropName="checked"
                tooltip="Toggle to activate or deactivate this role"
              >
                <Switch
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                  loading={roleStatusUpdating}
                  onChange={handleRoleStatusSwitch}
                />
              </Form.Item>
              <Form.Item
                label="System Role"
                name="is_system_role"
                valuePropName="checked"
                tooltip="System roles are protected and typically seeded by the platform"
              >
                <Switch
                  checkedChildren="System"
                  unCheckedChildren="Custom"
                  loading={roleSystemUpdating}
                  onChange={handleRoleSystemSwitch}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title={editingPermission ? 'Edit Permission' : 'Create Permission'}
        open={permissionModalVisible}
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        onCancel={() => {
          setPermissionModalVisible(false);
          setEditingPermission(null);
          permissionForm.resetFields();
        }}
        okText={editingPermission ? 'Update Permission' : 'Create Permission'}
        onOk={handleSubmitPermission}
      >
        <Form layout="vertical" form={permissionForm}>
          <Form.Item
            label="Display Name"
            name="display_name"
            rules={[{ required: true, message: 'Display name is required' }]}
          >
            <Input placeholder="e.g. Claims Approval" />
          </Form.Item>
          {catalogEnabled && !editingPermission ? (
            <>
              {/* A module with nothing unassigned used to be disabled, which made
                  it unreachable — and it is exactly the module you must reach to
                  add a new action to it. */}
              <Form.Item
                label="Module"
                name="catalog_module"
                rules={[{ required: true, message: 'Choose the module this permission belongs to' }]}
                tooltip="The product area this permission governs."
              >
                <Select
                  placeholder="Choose a module"
                  loading={catalogLoading}
                  onChange={handleCatalogModuleChange}
                  showSearch
                  optionFilterProp="label"
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '4px 0' }} />
                      <Button
                        type="link"
                        size="small"
                        icon={<PlusOutlined />}
                        style={{ width: '100%', textAlign: 'left' }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={openModuleDraft}
                      >
                        New module
                      </Button>
                    </>
                  )}
                  options={catalogModules.map((m) => ({
                    value: m.module_key,
                    label: m.label,
                    title: `${m.unassigned_count} of ${m.action_count} actions still have no permission`,
                  }))}
                />
              </Form.Item>
              <Form.Item
                label="Action"
                name="catalog_action"
                rules={[{ required: true, message: 'Choose the action to permit' }]}
                tooltip="Only actions that do not already have a permission are listed."
              >
                <Select
                  placeholder={selectedCatalogModule ? 'Choose an action' : 'Choose a module first'}
                  disabled={!selectedCatalogModule}
                  loading={catalogActionsLoading}
                  onChange={handleCatalogActionChange}
                  showSearch
                  optionFilterProp="label"
                  notFoundContent={
                    catalogActionsLoading
                      ? 'Loading…'
                      : 'Every action in this module already has a permission — add a new one below'
                  }
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '4px 0' }} />
                      <Button
                        type="link"
                        size="small"
                        icon={<PlusOutlined />}
                        style={{ width: '100%', textAlign: 'left' }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => openActionDraft()}
                      >
                        New action in this module
                      </Button>
                    </>
                  )}
                  options={catalogActions.map((a) => ({
                    value: a.id,
                    label: `${a.label} — ${a.permission_slug}`,
                  }))}
                />
              </Form.Item>
              <Form.Item
                label="Name (slug)"
                name="name"
                rules={[{ required: true, message: 'Choose a module and action above' }]}
                tooltip="Set by the catalog so the same capability cannot get two different slugs."
              >
                <Input disabled placeholder="Filled in from the chosen action" />
              </Form.Item>
              {/* resource/action are carried in hidden fields — the API still
                  takes them, they are just no longer typed by hand. */}
              <Form.Item name="resource" hidden><Input /></Form.Item>
              <Form.Item name="action" hidden><Input /></Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                label="Name (slug)"
                name="name"
                rules={[{ required: true, message: 'Permission name is required' }]}
              >
                <Input placeholder="e.g. claims.approve" disabled={Boolean(editingPermission)} />
              </Form.Item>
              <Form.Item
                label="Resource"
                name="resource"
                rules={[{ required: true, message: 'Resource is required' }]}
              >
                <Input placeholder="e.g. claims" disabled={Boolean(editingPermission)} />
              </Form.Item>
              <Form.Item
                label="Action"
                name="action"
                rules={[{ required: true, message: 'Action is required' }]}
              >
                <Input placeholder="e.g. approve" disabled={Boolean(editingPermission)} />
              </Form.Item>
            </>
          )}
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} placeholder="Optional description" />
          </Form.Item>
          {editingPermission && (
            <>
              <Divider />
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Advanced Controls
              </Text>
              <Form.Item
                label="Status"
                name="is_active"
                valuePropName="checked"
                tooltip="Toggle to activate or deactivate this permission"
              >
                <Switch
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                  loading={permissionStatusUpdating}
                  onChange={handlePermissionStatusSwitch}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* Catalog extension — a module is a product area, not a screen and not
          a permission. Nothing here grants anything: it describes a capability
          so a permission can then be created for it. */}
      <Modal
        title="New catalog module"
        open={moduleDraftOpen}
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        onCancel={() => setModuleDraftOpen(false)}
        okText="Add module"
        confirmLoading={catalogSaving}
        onOk={handleCreateModule}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="A module groups related capabilities"
          description="It is the product area an admin would look in — Facilities, Claims, Plans. Adding one grants nobody anything; it only gives new actions somewhere to live."
        />
        <Form layout="vertical" form={moduleDraftForm} initialValues={{ sort_order: 999 }}>
          <Form.Item label="Label" name="label" rules={[{ required: true, message: 'Label is required' }]}>
            <Input
              placeholder="e.g. Care Programs"
              onBlur={(e) => {
                if (!moduleDraftForm.getFieldValue('module_key')) {
                  moduleDraftForm.setFieldsValue({ module_key: slugify(e.target.value) });
                }
              }}
            />
          </Form.Item>
          <Form.Item
            label="Key"
            name="module_key"
            rules={[{ required: true, message: 'Key is required' }]}
            tooltip="Lowercase letters, digits and underscores. Permanent — route guards and seeded rows reference it."
          >
            <Input placeholder="e.g. care_programs" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="What this area covers" />
          </Form.Item>
          <Form.Item label="Sort order" name="sort_order" tooltip="Position in the module list. Built-in modules use 10–130.">
            <InputNumber min={0} max={9999} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`New action${selectedCatalogModule ? ` in ${catalogModules.find((m) => m.module_key === selectedCatalogModule)?.label || selectedCatalogModule}` : ''}`}
        open={actionDraftOpen}
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        onCancel={() => setActionDraftOpen(false)}
        okText="Add action"
        confirmLoading={catalogSaving}
        onOk={handleCreateAction}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="The slug is permanent"
          description="Route guards reference permissions by slug, so resource and action cannot be renamed later without breaking them. Reuse the module's existing resource unless this really is a different one."
        />
        <Form layout="vertical" form={actionDraftForm} initialValues={{ sort_order: 999 }}>
          <Form.Item label="Label" name="label" rules={[{ required: true, message: 'Label is required' }]}>
            <Input
              placeholder="e.g. Send login details"
              onBlur={(e) => {
                if (!actionDraftForm.getFieldValue('action')) {
                  actionDraftForm.setFieldsValue({ action: slugify(e.target.value, { allowDots: true }) });
                }
              }}
            />
          </Form.Item>
          <Form.Item
            label="Resource"
            name="resource"
            rules={[{ required: true, message: 'Resource is required' }]}
            tooltip="The slug prefix. Pre-filled with the resource this module already uses most."
          >
            <Input placeholder="e.g. facilities" />
          </Form.Item>
          <Form.Item
            label="Action"
            name="action"
            rules={[{ required: true, message: 'Action is required' }]}
            tooltip="Dots are allowed for namespacing, e.g. branches.manage. Wildcards are not actions and are refused."
          >
            <Input placeholder="e.g. send_login_details" />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {() => {
              const slug = draftActionSlug(actionDraftForm.getFieldsValue());
              return slug ? (
                <div style={{ marginTop: -8, marginBottom: 16 }}>
                  <Text type="secondary">Permission slug will be </Text>
                  <Tag color="geekblue">{slug}</Tag>
                </div>
              ) : null;
            }}
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="What this action permits" />
          </Form.Item>
          <Form.Item label="Sort order" name="sort_order">
            <InputNumber min={0} max={9999} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={selectedRole ? `Manage Permissions – ${selectedRole.display_name || selectedRole.name}` : 'Manage Permissions'}
        open={permissionDrawerVisible}
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        onCancel={() => {
          setPermissionDrawerVisible(false);
          setSelectedRole(null);
          setAssignedPermissionKeys([]);
          setInitialPermissionKeys([]);
        }}
        okText="Save Changes"
        confirmLoading={permissionSyncLoading}
        onOk={handleSyncRolePermissions}
        width={960}
      >
        {permissionSyncLoading && !catalogPermissions.length ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spin />
          </div>
        ) : (
          <RolePermissionAssigner
            permissions={catalogPermissions}
            assignedKeys={assignedPermissionKeys}
            onChange={setAssignedPermissionKeys}
            disabled={permissionSyncLoading}
            catalogModules={catalogModules}
            catalogActions={allCatalogActions}
            roleUserType={selectedRole?.user_type}
          />
        )}
      </Modal>

      <Modal
        title="Clean RBAC Data"
        open={cleanupModalVisible}
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        onCancel={() => {
          setCleanupModalVisible(false);
          setCleanupConfirmText('');
          setCleanupScope('assignments');
        }}
        okText="Execute Cleanup"
        okType={cleanupScope === 'all' ? 'danger' : 'primary'}
        confirmLoading={cleanupLoading}
        onOk={handleRunCleanup}
        width={600}
        okButtonProps={{
          disabled: cleanupConfirmText !== 'RBAC_CLEAN_NOW',
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="warning"
            message="Destructive Operation"
            description="This will remove RBAC data. Make sure you want to proceed."
            showIcon
          />

          <div>
            <Text strong>Select cleanup scope:</Text>
            <Radio.Group
              value={cleanupScope}
              onChange={(e) => setCleanupScope(e.target.value)}
              style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              <Radio value="assignments">
                <Space direction="vertical" size={4}>
                  <Text strong>User-Role Assignments Only</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Removes all user-role assignments. Safe - roles and permissions remain intact.
                  </Text>
                </Space>
              </Radio>
              <Radio value="mappings">
                <Space direction="vertical" size={4}>
                  <Text strong>Assignments + Mappings</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Removes user-role assignments and role-permission mappings. Roles and permissions remain.
                  </Text>
                </Space>
              </Radio>
              <Radio value="all">
                <Space direction="vertical" size={4}>
                  <Text strong style={{ color: '#ff4d4f' }}>Everything (Destructive)</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Removes all RBAC data including roles and permissions. Use with extreme caution.
                  </Text>
                  <Text type="danger" style={{ fontSize: 12 }}>
                    ⚠️ This will delete all RBAC data. You will need to re-seed everything.
                  </Text>
                </Space>
              </Radio>
            </Radio.Group>
          </div>

          {cleanupScope === 'all' && (
            <Alert
              type="error"
              message="Destructive Operation"
              description="This will delete all RBAC data including roles and permissions. You will need to re-seed everything after cleanup."
              showIcon
            />
          )}

          <Divider />

          <div>
            <Text strong>Confirmation:</Text>
            <Text type="secondary" style={{ display: 'block', marginTop: 8, marginBottom: 8 }}>
              Type <code>RBAC_CLEAN_NOW</code> to confirm:
            </Text>
            <Input
              value={cleanupConfirmText}
              onChange={(e) => setCleanupConfirmText(e.target.value)}
              placeholder="RBAC_CLEAN_NOW"
              disabled={cleanupLoading}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default RBACAdmin;

